'use client';

import { useState, useEffect } from 'react';
import MarketOverviewCard from './MarketOverviewCard';
import PriceChart from './PriceChart';
import AnalystInsights from './AnalystInsights';
import EconomicCalendar from './EconomicCalendar';
import MarketSentiment from './MarketSentiment';
import ResearchReports from './ResearchReports';
import TradingSignals from './TradingSignals';
import NewsUpdates from './NewsUpdates';
import { fetchKitcoPrices, convertToMarketMetrics, type MarketMetric } from '@/utils/kitcoApi';

const MarketIntelligenceInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [chartType, setChartType] = useState<'line' | 'area'>('line');
  const [marketMetrics, setMarketMetrics] = useState<MarketMetric[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  // Fetch prices on mount and every 8 hours
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setIsLoadingPrices(true);
        const prices = await fetchKitcoPrices();
        const metrics = convertToMarketMetrics(prices);
        
        // Add market volume as a custom metric
        metrics.push({
          label: 'Market Volume',
          value: '$2.4B',
          change: '+5.2%',
          trend: 'up' as const,
          icon: 'ChartBarIcon'
        });
        
        setMarketMetrics(metrics);
        setLastUpdate(new Date().toLocaleTimeString());
      } catch (error) {
        console.error('Error fetching metals.dev prices:', error);
      } finally {
        setIsLoadingPrices(false);
      }
    };

    fetchPrices();
    
    // Update prices every 8 hours (28800000 milliseconds)
    const interval = setInterval(fetchPrices, 28800000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const priceChartData = [
  { date: 'Dec 1', gold: 2020, silver: 24.2, copper: 8500 },
  { date: 'Dec 4', gold: 2025, silver: 24.4, copper: 8520 },
  { date: 'Dec 7', gold: 2018, silver: 24.1, copper: 8480 },
  { date: 'Dec 10', gold: 2030, silver: 24.5, copper: 8510 },
  { date: 'Dec 13', gold: 2035, silver: 24.6, copper: 8530 },
  { date: 'Dec 16', gold: 2028, silver: 24.3, copper: 8495 },
  { date: 'Dec 19', gold: 2040, silver: 24.7, copper: 8540 },
  { date: 'Dec 22', gold: 2045, silver: 24.8, copper: 8545 },
  { date: 'Dec 25', gold: 2042, silver: 24.75, copper: 8538 },
  { date: 'Dec 28', gold: 2048, silver: 24.85, copper: 8542 }];


  const analystInsights = [
  {
    id: 1,
    title: 'Gold Prices Expected to Surge Amid Global Economic Uncertainty',
    analyst: 'Dr. Sarah Mitchell',
    analystImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    analystImageAlt: 'Professional woman with brown hair in navy blazer smiling at camera in office setting',
    date: 'Dec 7, 2025',
    category: 'Gold Analysis',
    summary: 'Central bank policies and geopolitical tensions are creating favorable conditions for gold investment. Technical indicators suggest a potential breakout above $2,100 in Q1 2026.',
    sentiment: 'bullish' as const,
    readTime: '5 min read'
  },
  {
    id: 2,
    title: 'Uganda Mining Sector Shows Strong Growth Potential',
    analyst: 'James Okello',
    analystImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10d17e2d1-1763293910418.png",
    analystImageAlt: 'African businessman in gray suit with confident smile in modern office',
    date: 'Dec 6, 2025',
    category: 'Regional Markets',
    summary: 'New mining regulations and increased foreign investment are positioning Uganda as a key player in East African precious metals production. Export volumes to UAE increased 34% year-over-year.',
    sentiment: 'bullish' as const,
    readTime: '7 min read'
  },
  {
    id: 3,
    title: 'Silver Market Consolidation: What Traders Need to Know',
    analyst: 'Michael Chen',
    analystImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c682de70-1765037662781.png",
    analystImageAlt: 'Asian man in white shirt with glasses working at desk with laptop',
    date: 'Dec 5, 2025',
    category: 'Silver Analysis',
    summary: 'Silver prices are consolidating in a tight range. Industrial demand remains strong, but short-term price action suggests caution before major position entries.',
    sentiment: 'neutral' as const,
    readTime: '4 min read'
  }];


  const economicEvents = [
  {
    id: 1,
    date: 'Dec 8',
    time: '14:00',
    event: 'Federal Reserve Interest Rate Decision',
    country: 'USA',
    impact: 'high' as const,
    forecast: '5.50%',
    previous: '5.50%'
  },
  {
    id: 2,
    date: 'Dec 9',
    time: '09:30',
    event: 'Uganda GDP Growth Rate',
    country: 'UGA',
    impact: 'medium' as const,
    forecast: '6.2%',
    previous: '5.8%'
  },
  {
    id: 3,
    date: 'Dec 10',
    time: '12:00',
    event: 'Gold Reserves Report',
    country: 'Global',
    impact: 'high' as const,
    forecast: '35,244 tonnes',
    previous: '35,220 tonnes'
  },
  {
    id: 4,
    date: 'Dec 11',
    time: '10:00',
    event: 'UAE Trade Balance',
    country: 'UAE',
    impact: 'medium' as const,
    forecast: 'AED 12.5B',
    previous: 'AED 11.8B'
  }];


  const sentimentData = [
  { name: 'Bullish', value: 52, color: '#10B981' },
  { name: 'Neutral', value: 28, color: '#6B7280' },
  { name: 'Bearish', value: 20, color: '#EF4444' }];


  const researchReports = [
  {
    id: 1,
    title: 'Q4 2025 Precious Metals Market Outlook: Opportunities in East African Gold',
    author: 'UgandaGold Research Team',
    publishDate: 'Dec 1, 2025',
    category: 'Market Analysis',
    pages: 45,
    downloadCount: 1247,
    rating: 5,
    isPremium: true
  },
  {
    id: 2,
    title: 'Ethical Sourcing Impact Report: Uganda Mining Communities 2025',
    author: 'Dr. Emily Nakato',
    publishDate: 'Nov 28, 2025',
    category: 'ESG Research',
    pages: 32,
    downloadCount: 892,
    rating: 5,
    isPremium: true
  },
  {
    id: 3,
    title: 'Uganda-UAE Trade Corridor: Precious Metals Export Analysis',
    author: 'International Trade Institute',
    publishDate: 'Nov 25, 2025',
    category: 'Trade Reports',
    pages: 28,
    downloadCount: 654,
    rating: 4,
    isPremium: false
  },
  {
    id: 4,
    title: 'Technical Analysis: Gold Price Patterns and Forecasting Models',
    author: 'Marcus Thompson',
    publishDate: 'Nov 22, 2025',
    category: 'Technical Analysis',
    pages: 38,
    downloadCount: 1105,
    rating: 5,
    isPremium: false
  }];


  const tradingSignals = [
  {
    id: 1,
    metal: 'Gold (XAU/USD)',
    action: 'buy' as const,
    strength: 'strong' as const,
    targetPrice: '$2,085',
    stopLoss: '$2,035',
    timeframe: '1D',
    confidence: 85
  },
  {
    id: 2,
    metal: 'Silver (XAG/USD)',
    action: 'hold' as const,
    strength: 'moderate' as const,
    targetPrice: '$25.50',
    stopLoss: '$24.20',
    timeframe: '4H',
    confidence: 68
  },
  {
    id: 3,
    metal: 'Copper (HG)',
    action: 'sell' as const,
    strength: 'moderate' as const,
    targetPrice: '$8,480',
    stopLoss: '$8,580',
    timeframe: '1D',
    confidence: 72
  }];


  const newsUpdates = [
  {
    id: 1,
    title: 'Central Banks Increase Gold Reserves by 15% in Q4 2025',
    source: 'Reuters',
    publishTime: '2 hours ago',
    category: 'Global Markets',
    image: "https://images.unsplash.com/photo-1718752773197-37ade541a72b",
    imageAlt: 'Stack of gold bars arranged on dark surface with dramatic lighting',
    summary: 'Major central banks worldwide have significantly increased their gold holdings, signaling continued confidence in precious metals as a hedge against economic uncertainty.',
    impact: 'high' as const
  },
  {
    id: 2,
    title: 'Uganda Announces New Mining Regulations to Boost Ethical Sourcing',
    source: 'East African Business',
    publishTime: '5 hours ago',
    category: 'Regional News',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1071e5c51-1764732294127.png",
    imageAlt: 'Mining equipment and workers at open pit gold mine in Uganda with safety gear',
    summary: 'The Ugandan government has introduced comprehensive regulations aimed at ensuring transparent and ethical precious metals extraction, attracting international investors.',
    impact: 'high' as const
  },
  {
    id: 3,
    title: 'Silver Industrial Demand Reaches Record High in Manufacturing Sector',
    source: 'Bloomberg',
    publishTime: '8 hours ago',
    category: 'Commodities',
    image: "https://images.unsplash.com/photo-1639454276085-9f55d2db6570",
    imageAlt: 'Silver ingots and coins displayed on reflective surface with industrial background',
    summary: 'Growing demand from solar panel and electric vehicle manufacturers is driving silver prices higher, with analysts predicting sustained growth through 2026.',
    impact: 'medium' as const
  }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:278:6" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="278" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:279:8" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="279" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20pt-24%20pb-16%20px-4%20lg%3Apx-8%22%7D" className="w-full pt-24 pb-16 px-4 lg:px-8">
          <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:280:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="280" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
            <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:281:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="281" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
              <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:282:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="282" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-surface%20rounded%20w-1%2F3%22%7D" className="h-8 bg-surface rounded w-1/3" />
              <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:283:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="283" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-surface%20rounded%22%7D" className="h-64 bg-surface rounded" />
              <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:284:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="284" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:285:16" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="285" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded%22%7D" className="h-96 bg-surface rounded" />
                <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:286:16" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="286" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded%22%7D" className="h-96 bg-surface rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:296:4" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="296" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:297:6" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="297" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20pt-24%20pb-16%20px-4%20lg%3Apx-8%22%7D" className="w-full pt-24 pb-16 px-4 lg:px-8">
        <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:298:8" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="298" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20space-y-8%22%7D" className="max-w-7xl mx-auto space-y-8">
          <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:299:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="299" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20items-start%20sm%3Aitems-center%20justify-between%20space-y-4%20sm%3Aspace-y-0%22%7D" className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:300:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="300" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h1 data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:301:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="301" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Market%20Intelligence%20Center%22%7D" className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Market Intelligence Center
              </h1>
              <p data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:304:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="304" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Professional-grade%20analysis%20and%20insights%20for%20informed%20trading%20decisions%22%7D" className="text-text-secondary">
                Professional-grade analysis and insights for informed trading decisions
              </p>
              {lastUpdate && (
                <p data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:308:16" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="308" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-tertiary%20mt-1%22%7D" className="text-sm text-text-tertiary mt-1">
                  {isLoadingPrices ? (
                    <span data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:310:20" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="310" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%22%2C%22textContent%22%3A%22Updating%20prices...%22%7D" className="flex items-center">
                      <svg data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:311:22" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="311" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-3%20w-3%20mr-1%22%7D" className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:312:24" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="312" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D" className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:313:24" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="313" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D" className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating prices...
                    </span>
                  ) : (
                    <>Live prices from metals.dev API • Last updated: {lastUpdate} • Refreshes every 8 hours</>
                  )}
                </p>
              )}
            </div>
            <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:323:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="323" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
              <button data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:324:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="324" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Line%20Chart%22%7D"
                onClick={() => setChartType('line')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                chartType === 'line' ? 'bg-primary text-primary-foreground' : 'bg-surface text-text-secondary hover:bg-muted'}`
                }>

                Line Chart
              </button>
              <button data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:332:14" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="332" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Area%20Chart%22%7D"
                onClick={() => setChartType('area')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                chartType === 'area' ? 'bg-primary text-primary-foreground' : 'bg-surface text-text-secondary hover:bg-muted'}`
                }>

                Area Chart
              </button>
            </div>
          </div>

          <MarketOverviewCard data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:343:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="343" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="MarketOverviewCard" data-component-content="%7B%22elementName%22%3A%22MarketOverviewCard%22%7D" metrics={marketMetrics} />

          <PriceChart data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:345:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="345" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="PriceChart" data-component-content="%7B%22elementName%22%3A%22PriceChart%22%7D" data={priceChartData} chartType={chartType} />

          <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:347:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="347" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AnalystInsights data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:348:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="348" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="AnalystInsights" data-component-content="%7B%22elementName%22%3A%22AnalystInsights%22%7D" insights={analystInsights} />
            <EconomicCalendar data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:349:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="349" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="EconomicCalendar" data-component-content="%7B%22elementName%22%3A%22EconomicCalendar%22%7D" events={economicEvents} />
          </div>

          <MarketSentiment data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:352:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="352" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="MarketSentiment" data-component-content="%7B%22elementName%22%3A%22MarketSentiment%22%7D"
            data={sentimentData}
            overallSentiment="Bullish"
            sentimentScore={52} />


          <div data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:358:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="358" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ResearchReports data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:359:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="359" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="ResearchReports" data-component-content="%7B%22elementName%22%3A%22ResearchReports%22%7D" reports={researchReports} />
            <TradingSignals data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:360:12" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="360" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="TradingSignals" data-component-content="%7B%22elementName%22%3A%22TradingSignals%22%7D" signals={tradingSignals} />
          </div>

          <NewsUpdates data-component-id="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx:363:10" data-component-path="src/app/market-intelligence-center/components/MarketIntelligenceInteractive.tsx" data-component-line="363" data-component-file="MarketIntelligenceInteractive.tsx" data-component-name="NewsUpdates" data-component-content="%7B%22elementName%22%3A%22NewsUpdates%22%7D" news={newsUpdates} />
        </div>
      </div>
    </div>);

};

export default MarketIntelligenceInteractive;